<script>
	import Card from '@smui/card';
	import Select, { Option } from '@smui/select';
	import Tab, { Icon, Label } from '@smui/tab';
	import TabBar from '@smui/tab-bar';
	import Input from '@smui/textfield';
	import HelperText from '@smui/textfield/helper-text';
	import { _ } from 'svelte-i18n';
	import StepWizard from 'svelte-step-wizard';
	import { writable } from 'svelte/store';
	import FieldLoading from '../../../..//shared/components/common/Loading/FieldLoading.svelte';
	import FormLoading from '../../../..//shared/components/common/Loading/FormLoading.svelte';
	import Survey from '../../../..//shared/components/common/Survey/Survey.svelte';
	import QuestionarioRisposte from '../../../../shared/models/Questionario/QuestionarioRisposte';
	import Sede from '../../../../shared/models/Sede/Sede';
	import { hasRoleSysAdmin } from '../../../../shared/stores';

	export let params = {
		id: '0',
	};

	let [data, loading, error] = QuestionarioRisposte.getByAnonymous(params.id);
	let [dataSedi, loadingSedi, errorSedi] = [writable({ aziendaEnteId: $data.aziendaEnteId }), writable(false), null];
	let [dataQuestionarioCompletato, loadingQuestionarioCompletato, errorQuestionarioCompletato] = [null, writable(false), null];

	let jsonData = '';
	let tabs = [
        {
            icon: 'poll',
            label: 'Questionario',
        }
    ];

	let companyLogo = null;

	$: {
		if ($hasRoleSysAdmin) {
			tabs.push({
				icon: 'data_object',
            label: 'JSON',
			});
		}
	}

	async function getCompanyLogo() {
		try {
			const response = await fetch(`api/aziendaente/getlogo/${$data.aziendaEnteId}`);
			if (response.ok) {
				const result = await response.json();
				if (result.response && result.response.content) {
					companyLogo = `data:${result.response.mime};base64,${result.response.content}`;
				}
			}
		} catch (error) {
			console.error('Error loading company logo:', error);
		}
    }

	let active = tabs[0];
	let surveyComponent;
	let isEditingJson = false;

	$: {
		if ($data && $data.questionarioRisposteJson && !isEditingJson) {
			try {
				jsonData = JSON.stringify(JSON.parse($data.questionarioRisposteJson), null, 2);
			} catch (e) {
				console.error('Errore nel parsing del JSON:', e);
				jsonData = $data.questionarioRisposteJson || '';
			}
		}
	}

	data.subscribe((value) => {
		if (value.aziendaEnteId && !selectedSede?.id > 0) {
			[dataSedi, loadingSedi, errorSedi] = Sede.getListByAnonymous({ aziendaEnteId: $data.aziendaEnteId });
			getCompanyLogo();
		}
	});

	$: selectedSede = $data?.sedeId ? $dataSedi?.response?.find((x) => x.id === $data.sedeId) : {};
	$: if ($data && $dataSedi?.response?.length == 1) {
		selectedSede = $dataSedi.response[0];
		$data.sedeId = selectedSede.id;
	}

	function submit() {
		return ([dataQuestionarioCompletato, loadingQuestionarioCompletato, errorQuestionarioCompletato] =
			QuestionarioRisposte.saveByAnonymous($data));
	}

	function handleJsonChange() {
		isEditingJson = true;
		try {
			const parsedJson = JSON.parse(jsonData);
			$data.questionarioRisposteJson = JSON.stringify(parsedJson);
			if (surveyComponent) {
				surveyComponent.updateFromJSON(jsonData);
			}
		} catch (e) {
			console.error('JSON non valido:', e);
		}
		isEditingJson = false;
	}

	function handleSurveyDataChange(event) {
		if (!isEditingJson) {
			$data.questionarioRisposteJson = event.detail.stringifiedData;
			jsonData = JSON.stringify(JSON.parse(event.detail.stringifiedData), null, 2);
		}
	}

	function handleTabChange(newTab) {
		active = newTab;
		if (newTab.label === 'JSON') {
			jsonData = JSON.stringify(JSON.parse($data.questionarioRisposteJson), null, 2);
		} else if (newTab.label === 'Questionario') {
			if (surveyComponent) {
				surveyComponent.updateFromJSON($data.questionarioRisposteJson);
			}
		}
	}

	function isQuestionarioAttivo(questionario) {
        if (!questionario || !questionario.dataInizioValidita || !questionario.dataFineValidita) {
            return false;
        }
        
        const now = new Date();
        const dataInizio = new Date(questionario.dataInizioValidita);
        const dataFine = new Date(questionario.dataFineValidita);
        
        return now >= dataInizio && now <= dataFine;
    }
</script>

<Card class="md:px-8 md:py-8" padded>
	{#if companyLogo}
        <div class="flex justify-center mb-4">
            <img src="{companyLogo}" alt="Company Logo" class="max-h-40 h-auto max-w-full" />
        </div>
    {/if}
	{#if $loading}
		<FormLoading />
	{:else if $dataQuestionarioCompletato && $dataQuestionarioCompletato.id > 0}
		<div class="pt-4 text-center">
			<span class="pt-4 text-2xl">{$_('page.survey.detail.survey_completed')}</span>
		</div>
	{:else if $data && !isQuestionarioAttivo($data)}
		<div class="pt-4 text-center">
			<span class="pt-4 text-2xl">L'indagine è terminata, grazie per aver partecipato.</span>
		</div>
	{:else}
		<StepWizard initialStep="{1}">
			<StepWizard.Step num="{1}" let:nextStep>
				<div class="grid grid-cols-1 px-4 pt-4">
					<div class="flex col-start-2" style="text-align: end">
						<button disabled={!selectedSede?.id || selectedSede?.id == 0} type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{nextStep}">
							<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_forward_ios</span>
						</button>
					</div>
				</div>
				<span class="p-4">Seleziona la sede dal seguente menù a tendina</span>
				<div class="md:grid-cols-3 grid grid-cols-1">
					{#if $loadingSedi}
						<FieldLoading customClass="md:col-start-2" />
					{:else}
						<Select
							label="{$_('page.survey.detail.location')}"
							key="{(sede) => `${(sede && sede.id) || ''}`}"
							bind:value="{selectedSede}"
							class="md:col-start-2"
						>
							{#if $dataSedi.response}
								{#each $dataSedi.response as sede (sede.id)}
									{#if sede.denominazione}
										<Option
											value="{sede}"
											on:click="{() => {
												$data.sedeId = sede.id;
											}}">{sede.denominazione}</Option
										>
									{/if}
								{/each}
							{/if}
						</Select>
					{/if}
				</div>
			</StepWizard.Step>
			<StepWizard.Step num="{2}" let:previousStep let:nextStep>
				<div class="grid grid-cols-2 px-4 pt-4">
					<div class="justify-self-start col-start-1">
						<button type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{previousStep}">
							<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_back_ios</span>
						</button>
					</div>
					{#if $data.questionario.includiDatiFacoltativi}
						<div class="justify-self-end col-start-2">
							<button
								type="button"
								disabled="{!$data.questionarioRisposteJson}"
								class="btn-white h-14 w-14 sm:h-16 sm:w-16"
								on:click="{nextStep}"
							>
								<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_forward_ios</span>
							</button>
						</div>
					{/if}
				</div>

				<div class="md:grid-cols-4 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
					<div class="md:col-start-1 md:col-end-5 pt-4">
						<TabBar {tabs} let:tab bind:active on:MDCTabBar:activated={() => handleTabChange(active)}>
							<Tab {tab} minWidth>
								<Icon class="material-icons">{tab.icon}</Icon>
								<Label>{tab.label}</Label>
							</Tab>
						</TabBar>

						{#if active.label === 'Questionario'}
							<Survey
								bind:this={surveyComponent}
								bind:stringifiedJson="{$data.questionario.questionarioJson}"
								bind:stringifiedData="{$data.questionarioRisposteJson}"
								showNavigationButtons="{true}"
								submitFunction="{() => $data.questionario.includiDatiFacoltativi ? nextStep() : submit()}"
								on:dataChange="{handleSurveyDataChange}"
							/>
						{:else if active.label === 'JSON'}
							<Input
								textarea
								bind:value={jsonData}
								on:focus={() => isEditingJson = true}
								on:blur={() => {
									isEditingJson = false;
									handleJsonChange();
								}}
								input$placeholder="JSON del Questionario"
								input$rows="20"
								class="w-full mt-4"
							>
								<HelperText slot="helper">Modifica il JSON per aggiornare il questionario</HelperText>
							</Input>
						{/if}
					</div>
				</div>
			</StepWizard.Step>
			{#if $data.questionario.includiDatiFacoltativi}
				<StepWizard.Step num="{3}" let:previousStep>
					<div class="grid grid-cols-2 px-4 pt-4">
						<div class="justify-self-start col-start-1">
							<button type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{previousStep}">
								<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_back_ios</span>
							</button>
						</div>
						<div class="justify-self-end col-start-2">
							<button type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{submit}">
								<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">save</span>
							</button>
						</div>
					</div>
					<span class="p-4">Qualora lo desideri, puoi lasciarci le informazioni facoltative che trovi di seguito. Ciò ti consentirà di ricevere le credenziali di accesso a questa piattaforma, tramite la quale potrai visualizzare in qualsiasi momento le risposte date al questionario e ricevere le notizie sulla mobilità dalla tua azienda. Altrimenti, clicca direttamente sull'icona in alto a destra per salvare le risposte al questionario in modalità anonima.</span>
					<div class="md:grid-cols-3 grid grid-cols-1">
						<Input label="{$_('page.survey.detail.name')}" bind:value="{$data.nome}" class="md:col-start-2" />
						<Input label="{$_('page.survey.detail.surname')}" bind:value="{$data.cognome}" class="md:col-start-2" />
						<Input label="{$_('page.survey.detail.email')}" bind:value="{$data.email}" type="email" class="md:col-start-2" />
					</div>
				</StepWizard.Step>
			{/if}
		</StepWizard>
	{/if}
</Card>
